import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { FaTractor } from 'react-icons/fa';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { apiRequest } from '../config/api';
import { getSelectedFarm } from '../utils/persistentStorage';
import AccountSelectionModal, { SelectableItem } from './AccountSelectionModal';
import { useTranslation } from 'react-i18next';

interface CorporateAccount {
  id: string;
  companyName: string;
}

interface IndividualAccount {
  id: string;
  accountName: string;
  isPaidAccount: boolean;
  isActive?: boolean;
  corporateAccount?: {
    id: string;
    companyName: string;
  };
  farm?: {
    id: string;
    name: string;
    address?: string;
  };
}

interface AdminAccountSelectorProps {
  selectedAccount: any;
  onAccountSelect: (account: any) => void;
}

const AdminAccountSelector: React.FC<AdminAccountSelectorProps> = ({
  selectedAccount,
  onAccountSelect
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [corporateAccounts, setCorporateAccounts] = useState<CorporateAccount[]>([]);
  const [individualFarmAccounts, setIndividualFarmAccounts] = useState<IndividualAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    fetchAccountsData();
    restoreSelectedAccount();
  }, []);

  const restoreSelectedAccount = async () => {
    try {
      const savedAccount = await getSelectedFarm();
      if (savedAccount && !selectedAccount) {
        onAccountSelect(savedAccount);
      }
    } catch (error) {
      console.error('Error restoring selected account:', error);
    }
  };

  const fetchAccountsData = async () => {
    try {
      setLoading(true);
      setFetchError(null);

      const [individualRes, corporateRes] = await Promise.all([
        apiRequest<{ accounts: IndividualAccount[] }>('/api/individual-accounts'),
        apiRequest<{ accounts: CorporateAccount[] }>('/api/corporate-accounts'),
      ]);

      const individuals = (individualRes.accounts || []).map((account) => ({
        id: account.id,
        accountName: account.accountName,
        isPaidAccount: account.isPaidAccount,
        isActive: account.isActive,
        corporateAccount: account.corporateAccount
          ? {
              id: account.corporateAccount.id,
              companyName: account.corporateAccount.companyName,
            }
          : undefined,
        farm: account.farm,
      }));

      setIndividualFarmAccounts(individuals);
      setCorporateAccounts(corporateRes.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts data:', error);
      setFetchError(error instanceof Error ? error.message : 'Failed to load accounts');
      setIndividualFarmAccounts([]);
      setCorporateAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const toFarmItem = (account: IndividualAccount): SelectableItem => {
    const farmName = account.farm?.name || account.accountName;
    const subtitle = !account.farm
      ? t('selection.noFarmAssigned')
      : account.accountName && account.accountName !== farmName
        ? account.accountName
        : account.farm.address;

    return {
      id: account.id,
      name: farmName,
      subtitle,
      badge: account.isPaidAccount ? t('selection.paid') : t('selection.free'),
      badgeColor: account.isPaidAccount ? 'green' : 'gray',
      icon: <FaTractor className="size-4 text-emerald-600" />,
      metadata: { ...account, type: 'individual' },
      isActive: account.isActive !== false,
    };
  };

  const convertToSelectableItems = (): SelectableItem[] => {
    const independents: IndividualAccount[] = [];
    const byCorp = new Map<string, { name: string; farms: IndividualAccount[] }>();

    corporateAccounts.forEach((corp) => {
      byCorp.set(corp.id, { name: corp.companyName, farms: [] });
    });

    individualFarmAccounts.forEach((account) => {
      const corp = account.corporateAccount;
      if (corp?.id) {
        const group = byCorp.get(corp.id) || { name: corp.companyName, farms: [] };
        group.farms.push(account);
        byCorp.set(corp.id, group);
      } else {
        independents.push(account);
      }
    });

    const items: SelectableItem[] = [...byCorp.entries()]
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
      .map(([id, group]) => {
        const farms = [...group.farms].sort((a, b) =>
          (a.farm?.name || a.accountName).localeCompare(b.farm?.name || b.accountName)
        );
        return {
          id,
          name: group.name,
          subtitle: t('selection.farmCount', { count: farms.length }),
          badge: t('selection.corporate'),
          badgeColor: 'blue' as const,
          icon: <HiOutlineOfficeBuilding className="size-4 text-sky-600" />,
          metadata: {
            id,
            companyName: group.name,
            type: 'corporate',
            individualAccounts: farms,
          },
          isActive: true,
          children: farms.map(toFarmItem),
        };
      });

    independents
      .sort((a, b) =>
        (a.farm?.name || a.accountName).localeCompare(b.farm?.name || b.accountName)
      )
      .forEach((account) => items.push(toFarmItem(account)));

    return items;
  };

  const handleItemSelect = (item: SelectableItem | null) => {
    if (!item) {
      onAccountSelect(null);
      return;
    }

    if (item.metadata) {
      onAccountSelect(item.metadata);
      if (item.metadata.type === 'corporate' && item.metadata.id) {
        navigate(`/admin/corporate/${item.metadata.id}`);
      }
    }
  };

  const getDisplayText = () => {
    if (!selectedAccount) return t('selection.selectAccount');

    if (selectedAccount.type === 'corporate') {
      return `🏢 ${selectedAccount.companyName}`;
    }

    return `🏡 ${selectedAccount.farm?.name || selectedAccount.accountName}`;
  };

  const getSelectedItem = (): SelectableItem | null => {
    if (!selectedAccount) return null;
    for (const item of convertToSelectableItems()) {
      if (item.id === selectedAccount.id) return item;
      const child = item.children?.find((entry) => entry.id === selectedAccount.id);
      if (child) return child;
    }
    return null;
  };

  const hasAccounts = individualFarmAccounts.length > 0 || corporateAccounts.length > 0;

  return (
    <>
      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-slate-600">
          {t('selection.accountSelection')}
        </label>

        <button
          onClick={() => setShowModal(true)}
          className="flex h-8 w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-sm hover:bg-slate-50 disabled:opacity-50"
          disabled={loading}
        >
          <span className={`truncate ${selectedAccount ? 'text-slate-900' : 'text-slate-500'}`}>
            {loading ? t('selection.loadingAccounts') : getDisplayText()}
          </span>
          <FiChevronDown className="size-3.5 shrink-0 text-slate-400" />
        </button>

        {!loading && fetchError && (
          <p className="text-xs text-red-600">{fetchError}</p>
        )}

        {!loading && !fetchError && !hasAccounts && (
          <p className="text-xs text-gray-500">{t('selection.noAccountsAvailable')}</p>
        )}
      </div>

      <AccountSelectionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSelect={handleItemSelect}
        items={convertToSelectableItems()}
        selectedItem={getSelectedItem()}
        title={t('selection.selectAccountOrFarm')}
        searchPlaceholder={t('selection.searchPlaceholderAccount')}
        emptyMessage={t('selection.noAccountsFound')}
        showClearSelection={true}
        allowGroupExpansion={true}
        showActiveFilter={false}
      />
    </>
  );
};

export default AdminAccountSelector;
